import React, { useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { toast } from "react-toastify";
import { useMeeting, usePubSub } from "@videosdk.live/react-sdk";
import { useSiteContext } from "../../context/SiteContext";

const PollVotingModal = ({ poll, isOpen, onClose, onVoteSubmitted }) => {
  const mMeeting = useMeeting();
  const { user, meetingSession } = useSiteContext();
  const { publish: publishPollVote } = usePubSub("POLL_VOTE");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [voteResults, setVoteResults] = useState(null);

  // Listen for poll results updates from moderator
  usePubSub("POLL_VOTE", {
    onMessageReceived: (data) => {
      console.log("POLL_VOTE results received in modal", data);
      const rawMessage = data.message || data;
      const messageData =
        typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      if (!messageData) return;
      const { type, poll: updatedPoll } = messageData;

      // Update results if this is a results update for the current poll
      if (
        type === "results_update" &&
        updatedPoll &&
        updatedPoll.id === poll?.id
      ) {
        console.log("Updating poll results in modal", updatedPoll);
        setVoteResults(updatedPoll);
      }
    },
  });

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  // Reset state when poll changes
  useEffect(() => {
    if (poll) {
      setSelectedOptions([]);
      setHasVoted(false);
      setVoteResults(null);
      // Check if user has already voted
      checkUserVote();
    }
  }, [poll?.id]);

  // Auto-close timer if enabled
  useEffect(() => {
    if (
      poll?.settings?.autoCloseAfter &&
      poll?.settings?.autoCloseSeconds &&
      isOpen &&
      !hasVoted
    ) {
      const timer = setTimeout(() => {
        handleClose();
      }, poll.settings.autoCloseSeconds * 1000);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    poll?.settings?.autoCloseAfter,
    poll?.settings?.autoCloseSeconds,
    isOpen,
    hasVoted,
  ]);

  const checkUserVote = async () => {
    // TODO: Implement check if user has already voted
    // This would require an API endpoint to check vote status
    // For now, we assume user hasn't voted
  };

  const handleOptionToggle = (optionIndex) => {
    if (!poll || hasVoted) return;

    if (poll.settings?.allowMultipleAnswers) {
      // Multiple selection
      setSelectedOptions((prev) => {
        if (prev.includes(optionIndex)) {
          return prev.filter((idx) => idx !== optionIndex);
        } else {
          return [...prev, optionIndex];
        }
      });
    } else {
      // Single selection
      setSelectedOptions([optionIndex]);
    }
  };

  const handleSkipVote = async () => {
    if (!poll || hasVoted) {
      handleClose();
      return;
    }

    const participantId = mMeeting?.localParticipant?.id;
    const participantName =
      mMeeting?.localParticipant?.displayName || user?.name || "Anonymous";

    if (!participantId) {
      toast.error("Unable to skip poll. Participant ID not found.");
      return;
    }

    setIsSubmitting(true);

    const skipData = {
      sessionId: meetingSession,
      meetingId: mMeeting?.meetingId,
      pollId: poll.id,
      participantId,
      participantName: poll.settings?.anonymousVoting
        ? "Anonymous"
        : participantName,
      timestamp: Date.now(),
    };

    try {
      await publishPollVote(
        JSON.stringify({
          type: "vote_skipped",
          pollId: poll.id,
          skip: skipData,
        })
      );

      setHasVoted(true);
      toast.info("You skipped this poll.");

      if (onVoteSubmitted) {
        onVoteSubmitted(poll.id);
      }

      setTimeout(() => {
        handleClose();
      }, 600);
    } catch (error) {
      console.error("Error publishing poll skip event:", error);
      toast.error(
        error.message || "An error occurred while skipping this poll."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitVote = async () => {
    if (!poll || selectedOptions.length === 0 || hasVoted) {
      toast.error("Please select at least one option.");
      return;
    }

    const participantId = mMeeting?.localParticipant?.id;
    const participantName =
      mMeeting?.localParticipant?.displayName || user?.name || "Anonymous";

    if (!participantId) {
      toast.error("Unable to submit vote. Participant ID not found.");
      return;
    }

    setIsSubmitting(true);

    const voteData = {
      sessionId: meetingSession,
      meetingId: mMeeting?.meetingId,
      pollId: poll.id,
      participantId,
      participantName: poll.settings?.anonymousVoting
        ? "Anonymous"
        : participantName,
      optionIndices: selectedOptions,
      timestamp: Date.now(),
    };

    try {
      await publishPollVote(
        JSON.stringify({
          type: "vote_submitted",
          pollId: poll.id,
          vote: voteData,
        })
      );

      setHasVoted(true);
      toast.success("Vote submitted successfully!");

      if (onVoteSubmitted) {
        onVoteSubmitted(poll.id);
      }

      if (!poll.settings?.shareResultsLive) {
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (error) {
      console.error("Error publishing POLL_VOTE event:", error);
      toast.error(
        error.message || "An error occurred while submitting your vote."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!poll) return null;

  // Normalize options - handle both string array and object array
  const options = poll.options || [];
  const normalizedOptions = options.map((opt, idx) => {
    const derivedIndex =
      typeof opt === "object" && opt !== null && typeof opt.index === "number"
        ? opt.index
        : idx;

    if (typeof opt === "string") {
      return { text: opt, index: derivedIndex };
    }
    // Handle object with text property or just use the object itself
    if (opt && typeof opt === "object") {
      return {
        ...opt,
        text: opt.text || opt.label || String(opt),
        index: derivedIndex,
      };
    }
    return { text: String(opt), index: derivedIndex };
  });

  // Calculate vote percentages for results display
  const getOptionPercentage = (optionIndex) => {
    if (!voteResults || !voteResults.options) return 0;
    const option = voteResults.options.find((opt) => opt.index === optionIndex);
    if (!option || voteResults.totalVotes === 0) return 0;
    return Math.round((option.votes / voteResults.totalVotes) * 100);
  };

  const totalVotesCount =
    voteResults?.totalVotes ?? voteResults?.votes?.length ?? 0;
  const skippedCount =
    voteResults?.skippedCount ?? voteResults?.skipped?.length ?? 0;
  const totalResponses = totalVotesCount + skippedCount;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-[#1a1a2e] border border-gray-600 p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-xl font-bold text-white">
                    Poll
                  </Dialog.Title>
                  {!poll.settings?.autoCloseAfter && (
                    <button
                      onClick={handleClose}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Question */}
                <div className="mb-6">
                  <p className="text-white text-lg font-medium">
                    {poll.question}
                  </p>
                  {poll.settings?.allowMultipleAnswers && (
                    <p className="text-gray-400 text-sm mt-1">
                      Select all that apply
                    </p>
                  )}
                </div>

                {/* Options */}
                {!hasVoted || !voteResults ? (
                  <div className="space-y-3 mb-6">
                    {normalizedOptions.map((option, index) => {
                      const isSelected = selectedOptions.includes(option.index);
                      return (
                        <button
                          key={index}
                          onClick={() => handleOptionToggle(option.index)}
                          disabled={hasVoted || isSubmitting}
                          className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                            isSelected
                              ? "border-[#7700ff] bg-[#7700ff]/20"
                              : "border-gray-600 bg-[#0f0f1e] hover:border-gray-500"
                          } ${
                            hasVoted || isSubmitting
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-white text-sm">
                              {option.text}
                            </span>
                            {isSelected && (
                              <svg
                                className="w-5 h-5 text-[#7700ff]"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  /* Results Display */
                  <div className="space-y-3 mb-6">
                    {normalizedOptions.map((option, index) => {
                      const percentage = getOptionPercentage(option.index);
                      const isSelectedOption = selectedOptions.includes(
                        option.index
                      );
                      return (
                        <div
                          key={index}
                          className={`w-full px-4 py-3 rounded-lg border-2 ${
                            isSelectedOption
                              ? "border-[#7700ff] bg-[#7700ff]/20"
                              : "border-gray-600 bg-[#0f0f1e]"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white text-sm">
                              {option.text}
                              {isSelectedOption && (
                                <span className="ml-2 text-[#7700ff] text-xs">
                                  (Your vote)
                                </span>
                              )}
                            </span>
                            <span className="text-gray-400 text-sm">
                              {percentage}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-[#7700ff] h-2 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          {voteResults.options?.[index]?.votes !==
                            undefined && (
                            <p className="text-gray-400 text-xs mt-1">
                              {voteResults.options[index].votes} vote
                              {voteResults.options[index].votes !== 1
                                ? "s"
                                : ""}
                            </p>
                          )}
                        </div>
                      );
                    })}
                    {totalVotesCount !== undefined && (
                      <p className="text-gray-400 text-sm text-center mt-4">
                        Votes: {totalVotesCount}
                      </p>
                    )}
                    {totalResponses ? (
                      <p className="text-gray-400 text-xs text-center">
                        Responses: {totalResponses}
                      </p>
                    ) : null}
                    {skippedCount ? (
                      <p className="text-gray-400 text-xs text-center">
                        Skipped: {skippedCount}
                      </p>
                    ) : null}
                  </div>
                )}

                {/* Action Buttons */}
                {!hasVoted && (
                  <div className="flex gap-3">
                    {!poll.settings?.autoCloseAfter && (
                      <button
                        onClick={handleSkipVote}
                        className="flex-1 px-4 py-2 rounded-lg bg-transparent border border-gray-600 text-white hover:bg-gray-700 transition-colors"
                        disabled={isSubmitting}
                      >
                        Skip
                      </button>
                    )}
                    <button
                      onClick={handleSubmitVote}
                      disabled={selectedOptions.length === 0 || isSubmitting}
                      className="flex-1 px-4 py-2 rounded-lg bg-[#7700ff] text-white hover:bg-[#7700ff]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? "Submitting..." : "Submit Vote"}
                    </button>
                  </div>
                )}

                {hasVoted &&
                  voteResults &&
                  !poll.settings?.shareResultsLive && (
                    <div className="flex justify-center">
                      <button
                        onClick={handleClose}
                        className="px-6 py-2 rounded-lg bg-[#7700ff] text-white hover:bg-[#7700ff]/80 transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  )}

                {/* Auto-close countdown */}
                {poll.settings?.autoCloseAfter &&
                  poll.settings?.autoCloseSeconds &&
                  !hasVoted && (
                    <p className="text-gray-400 text-xs text-center mt-4">
                      Poll will close in {poll.settings.autoCloseSeconds}{" "}
                      seconds
                    </p>
                  )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PollVotingModal;
